import {
  TravellerDetailsActionPayload,
  TRAVELLER_DETAILS,
  TRAVELLER_DETAILS_ERRORS,
  COVERME_TRAVELLER_DETAILS,
  COVERME_ORIGINAL_TRAVELLER_DETAILS
} from './types';
import { Dispatch } from 'redux';
import { range, get, reduce, merge, toString, isEmpty, trim, set } from 'lodash';
import { COVERME_STUDENT_TYPES, COVERME_TRAVELLER_TYPES } from '../quote-form';
import { checkTrue, dayjs } from '@trip-ux/common';

export const setTravellersDetails =
  (payload: TravellerDetailsActionPayload) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: TRAVELLER_DETAILS,
      payload
    });
  };
export const setTravellersDetailsError =
  (payload: TravellerDetailsActionPayload) =>
  (dispatch: Dispatch): void => {
    dispatch({
      type: TRAVELLER_DETAILS_ERRORS,
      payload
    });
  };
export const setCoverMeTravellersDetails = (values, formName) => (dispatch: Dispatch) => {
  const payload = convertCoverMeTravellersDetails(values, formName);
  dispatch({
    type: COVERME_TRAVELLER_DETAILS,
    payload
  });
};
export const setCoverMeContactDetails = (values, formName) => (dispatch: Dispatch) => {
  const payload = convertCoverMeContactDetails(values, formName);
  dispatch({
    type: COVERME_TRAVELLER_DETAILS,
    payload
  });
};

const getRelationship = (values, paxId) => {
  const hasRelationship = checkTrue(get(values, 'travellerDependentQn')) || get(values, 'familyConfirmation');
  return (
    hasRelationship && {
      relationship: get(values, `relationship${paxId}`)
    }
  );
};

const getEmailPhone = (values, paxId) => {
  const isVisitorToCanada = get(values, 'travellerType') === COVERME_TRAVELLER_TYPES.VISITOR_TO_CANADA;
  const isInternationalStudent = get(values, 'studentType') === COVERME_STUDENT_TYPES.INTERNATIONAL;
  return checkTrue(get(values, `emailPhoneSameAsPrimary${paxId}`))
    ? {
        emailPhoneSameAsPrimary: toString(get(values, `emailPhoneSameAsPrimary${paxId}`))
      }
    : {
        email: get(values, `email${paxId}`),
        phone: get(values, `phone${paxId}`),
        ...((isVisitorToCanada || isInternationalStudent) && {
          alternatePhone: get(values, `alternatePhone${paxId}`)
        })
      };
};

const getAddress = (values, paxId) => {
  return checkTrue(get(values, `addressSameAsPrimary${paxId}`))
    ? {
        addressSameAsPrimary: toString(get(values, `addressSameAsPrimary${paxId}`))
      }
    : {
        addressLine1: get(values, `addressLine1-${paxId}`),
        addressLine2: get(values, `addressLine2-${paxId}`),
        city: get(values, `city${paxId}`),
        postalCode: get(values, `postalCode${paxId}`),
        province: get(values, `province`)
      };
};

const getSchoolInfo = (values, paxId) => {
  return (
    get(values, 'travellerType') === COVERME_TRAVELLER_TYPES.STUDENT && {
      schoolName: get(values, `schoolName${paxId}`),
      schoolCity: get(values, `schoolCity${paxId}`)
    }
  );
};

export const convertCoverMeContactDetails = (values, formName) => {
  return reduce(
    range(values.travellerCount),
    (acc, curr) => {
      const paxId = curr + 1;
      const country = get(values, `homeCountry${paxId}`);
      return {
        ...acc,
        [`${formName}-${paxId}`]: {
          paxId,
          firstName: trim(get(values, `firstName${paxId}`)),
          lastName: trim(get(values, `lastName${paxId}`)),
          language: get(values, `language${paxId}`),
          ...(!isEmpty(country) && { homeCountry: country }),
          ...getRelationship(values, paxId),
          ...getEmailPhone(values, paxId),
          ...getAddress(values, paxId),
          ...getSchoolInfo(values, paxId),
          bill96Consent: get(values, `bill96Consent${paxId}`)
        }
      };
    },
    {}
  );
};
export const convertCoverMeTravellersDetails = (values, formName) => {
  return reduce(
    range(values.travellerCount),
    (acc, curr) => {
      const travellerDetails = {
        ...acc,
        [`${formName}-${curr + 1}`]: {
          firstName: trim(get(values, `firstName${curr + 1}`)),
          lastName: trim(get(values, `lastName${curr + 1}`)),
          paxId: curr + 1
        }
      };
      if (curr === 0) {
        merge(travellerDetails, {
          [`${formName}-${curr + 1}`]: {
            email: get(values, `email${curr + 1}`),
            phone: get(values, `phone${curr + 1}`),
            province: values.province
          }
        });
      }
      return travellerDetails;
    },
    {}
  );
};
export const setCoverMeOriginalTravellersDetails = (values, dateFormat?: string) => (dispatch: Dispatch) => {
  for (let i = 1; i <= get(values, 'travellerCount'); i++) {
    const firstName = `firstName${i}`;
    const lastName = `lastName${i}`;
    const dobKey = `dob${i}`;
    set(values, firstName, trim(get(values, firstName)));
    set(values, lastName, trim(get(values, lastName)));
    
    // Convert MUX DateOfBirth object format to string if needed
    const dobValue = get(values, dobKey);
    if (dobValue && typeof dobValue === 'object' && 'year' in dobValue) {
      const { year, month, day } = dobValue;
      if (year && month && day) {
        // Create a dayjs object and format it according to the configured date format
        const format = dateFormat || 'DD/MM/YYYY'; // Default to DD/MM/YYYY if not provided
        const dateObj = dayjs(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`, 'YYYY-MM-DD');
        if (dateObj.isValid()) {
          const formattedDob = dateObj.format(format);
          set(values, dobKey, formattedDob);
        }
      }
    }
  }
  set(values, 'employeeName', trim(get(values, 'employeeName')));
  const payload = values;

  dispatch({
    type: COVERME_ORIGINAL_TRAVELLER_DETAILS,
    payload
  });
};
